import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { FileText, Users, BarChart3, Building2 } from 'lucide-react';
import { Team, Player, Assessment } from '../types';
import { PlayerReportView } from '../components/PlayerReportView';
import { TeamAnalyticsDashboard } from '../components/TeamAnalyticsDashboard';
import { ClubAnalyticsDashboard } from '../components/ClubAnalyticsDashboard';
import { ParentReportView } from '../components/ParentReportView';
import { teamService } from '../services/teamService';
import { assessmentService } from '../services/assessmentService';
import { useAuth } from '../contexts/AuthContext';
import { LoadingSpinner } from '../components/LoadingSpinner';

type ReportType = 'player' | 'team' | 'club';
type ViewMode = 'coach' | 'parent' | 'admin';

export const ReportsPage: React.FC = () => {
  const { teamId, playerId } = useParams<{ teamId?: string; playerId?: string }>();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();

  const [reportType, setReportType] = useState<ReportType>(() => {
    if (playerId) return 'player';
    if (teamId) return 'team';
    return 'team';
  });

  const [selectedTeamId, setSelectedTeamId] = useState<string>(teamId || '');
  const [selectedPlayerId, setSelectedPlayerId] = useState<string>(playerId || '');
  const [teams, setTeams] = useState<Team[]>([]);
  const [players, setPlayers] = useState<Player[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
  const [selectedPlayer, setSelectedPlayer] = useState<Player | null>(null);
  const [assessments, setAssessments] = useState<Assessment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>('');

  const viewMode: ViewMode = (() => {
    if (searchParams.get('view') === 'parent' || user?.role === 'PARENT') return 'parent';
    if (user?.role === 'CLUB_ADMIN') return 'admin';
    return 'coach';
  })();

  useEffect(() => {
    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role]);

  useEffect(() => {
    if (selectedTeamId) {
      loadTeamData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTeamId]);

  useEffect(() => {
    if (selectedPlayerId) {
      loadPlayerData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlayerId]);

  const loadInitialData = async () => {
    try {
      setIsLoading(true);
      setError('');

      if (user?.role === 'PARENT') {
        if (playerId) {
          setSelectedPlayerId(playerId);
          setReportType('player');
        }
      } else {
        const teamsData = await teamService.getTeams();
        setTeams(teamsData);

        if (teamId && teamsData.length > 0) {
          const team = teamsData.find(t => t.id === teamId);
          if (team) {
            setSelectedTeam(team);
            setSelectedTeamId(teamId);
          }
        } else if (teamsData.length > 0 && !selectedTeamId) {
          setSelectedTeamId(teamsData[0].id);
          setSelectedTeam(teamsData[0]);
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to load data');
    } finally {
      setIsLoading(false);
    }
  };

  const loadTeamData = async () => {
    if (!selectedTeamId) return;
    try {
      const [teamData, playersData] = await Promise.all([
        teamService.getTeam(selectedTeamId),
        teamService.getTeamPlayers(selectedTeamId)
      ]);
      setSelectedTeam(teamData);
      setPlayers(playersData);

      if (playerId && playersData.length > 0) {
        const player = playersData.find(p => p.id === playerId);
        if (player) {
          setSelectedPlayer(player);
          setSelectedPlayerId(playerId);
          setReportType('player');
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to load team data');
    }
  };

  const loadPlayerData = async () => {
    if (!selectedPlayerId) return;
    try {
      const assessmentsData = await assessmentService.getPlayerAssessments(selectedPlayerId);
      setAssessments(assessmentsData);

      if (!selectedPlayer && players.length > 0) {
        const player = players.find(p => p.id === selectedPlayerId);
        if (player) setSelectedPlayer(player);
      }
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to load player data');
    }
  };

  const handleReportTypeChange = (newType: ReportType) => {
    setReportType(newType);
    setError('');
  };

  const handleTeamChange = (id: string) => {
    setSelectedTeamId(id);
    setSelectedPlayerId('');
    setSelectedPlayer(null);
    setAssessments([]);
    const team = teams.find(t => t.id === id);
    setSelectedTeam(team || null);
  };

  const handlePlayerChange = (id: string) => {
    setSelectedPlayerId(id);
    const player = players.find(p => p.id === id);
    setSelectedPlayer(player || null);
  };

  const canShowReportType = (type: ReportType): boolean => {
    switch (viewMode) {
      case 'parent': return type === 'player';
      case 'admin': return true;
      case 'coach': return type !== 'club';
      default: return false;
    }
  };

  const tabs: { type: ReportType; label: string; icon: React.ElementType }[] = [
    { type: 'player', label: 'Player Reports', icon: Users },
    { type: 'team', label: 'Team Analytics', icon: BarChart3 },
    { type: 'club', label: 'Club Analytics', icon: Building2 },
  ];

  const renderReportContent = () => {
    if (error) {
      return (
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-red-700">
          <p className="font-medium">Error</p>
          <p className="text-sm mt-1">{error}</p>
        </div>
      );
    }

    switch (reportType) {
      case 'player':
        if (!selectedPlayer) {
          return (
            <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
              <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-7 h-7 text-blue-600" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Select a player</h2>
              <p className="text-sm text-gray-500">Choose a team and player above to view their progress report.</p>
            </div>
          );
        }
        return viewMode === 'parent' ? (
          <ParentReportView player={selectedPlayer} assessments={assessments} />
        ) : (
          <PlayerReportView player={selectedPlayer} assessments={assessments} isParentView={false} />
        );

      case 'team':
        if (!selectedTeam) {
          return (
            <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
              <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <BarChart3 className="w-7 h-7 text-green-600" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Select a team</h2>
              <p className="text-sm text-gray-500">Choose a team above to view team analytics.</p>
            </div>
          );
        }
        return <TeamAnalyticsDashboard team={selectedTeam} players={players} />;

      case 'club':
        return <ClubAnalyticsDashboard clubId={user?.profile?.clubId || 'default-club'} />;

      default:
        return null;
    }
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="p-4 lg:p-8 max-w-7xl mx-auto">
      {/* Page Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-1">
          <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
            <FileText className="w-5 h-5 text-green-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Reports</h1>
            <p className="text-sm text-gray-500">Player progress reports and team analytics</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      {viewMode !== 'parent' && (
        <div className="flex gap-1 mb-6 border-b border-gray-200 overflow-x-auto">
          {tabs.filter(t => canShowReportType(t.type)).map(tab => {
            const Icon = tab.icon;
            const isActive = reportType === tab.type;
            return (
              <button
                key={tab.type}
                onClick={() => handleReportTypeChange(tab.type)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors min-h-[44px] ${
                  isActive
                    ? 'border-green-600 text-green-700'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>
      )}

      {/* Selectors */}
      {viewMode !== 'parent' && (reportType === 'player' || reportType === 'team') && teams.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">Team</label>
              <select
                value={selectedTeamId}
                onChange={(e) => handleTeamChange(e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none transition-colors min-h-[44px]"
              >
                <option value="">Select a team...</option>
                {teams.map(team => (
                  <option key={team.id} value={team.id}>
                    {team.name} ({team.ageGroup})
                  </option>
                ))}
              </select>
            </div>

            {reportType === 'player' && players.length > 0 && (
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">Player</label>
                <select
                  value={selectedPlayerId}
                  onChange={(e) => handlePlayerChange(e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none transition-colors min-h-[44px]"
                >
                  <option value="">Select a player...</option>
                  {players.map(player => (
                    <option key={player.id} value={player.id}>
                      {player.firstName} {player.lastName} ({player.position})
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Report Content */}
      {renderReportContent()}
    </div>
  );
};
